// This component provides a control panel for filtering and specifying
// parameters for Controlled Environment Agriculture (CEA) facilities. It
// allows users to select crop type, facility type, size, and technology
// level, and then search for baseline operational metrics for the nearest
// matching facility.

import axios from 'axios';
import * as React from 'react';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Accordion from 'react-bootstrap/Accordion';
import {findNearestFacility} from "../utils/utils";
import baselineFacilitiesMetrics from "../../../../map/filedata/prod/baseline_facility_metrics.json"

// Data structure for the facility filter form, including all selectable
// options for crop type, facility type, size, and technology level.
const facilityFormData = [
    {
        id: 'crop',
        title: "Crop Type",
        hasCheckbox: false,
        children: [
            {
                id: "Tomatoes",
                title: "Tomatoes",
                hasCheckbox: true,
            }, {
                id: "Spinach",
                title: "Spinach",
                hasCheckbox: true,
            }, {    
                id: "Zucchini",
                title: "Zucchini",
                hasCheckbox: true,
            }, {
                id: "Basil",
                title: "Basil",
                hasCheckbox: true,
            }, {
                id: "Broccoli",
                title: "Broccoli",
                hasCheckbox: true,
            }, {
                id: "Lettuce",
                title: "Lettuce",
                hasCheckbox: true,
            }, {
                id: "Roses",
                title: "Roses",
                hasCheckbox: true,
            }, {
                id: "Bell peppers",
                title: "Bell peppers",
                hasCheckbox: true,
            }, {    
                id: "Strawberries",
                title: "Strawberries",
                hasCheckbox: true,
            }
        ]
    }, {
        id: 'facilityType',
        title: "Facility Type",
        hasCheckbox: false,
        children: [
            {
                id: "Greenhouse",
                title: "Greenhouse",
                hasCheckbox: true,
            }, {
                id: "Hoop House",
                title: "Hoop House",
                hasCheckbox: true,
            }, {
                id: "Plant Factory",
                title: "Plant Factory",
                hasCheckbox: true,
            }
        ]
    }, {
        id: 'facilitySize',
        title: "Facility Size",
        hasCheckbox: false,
        children: [{
            id: "Small",
            title: "Small",
            hasCheckbox: true,
        }, {
            id: "Medium",
            title: "Medium",
            hasCheckbox: true,
        }, {
            id: "Large",
            title: "Large",
            hasCheckbox: true,
        }
        ]
    }, {
        id: 'facilityTech',
        title: "Technology Level",
        hasCheckbox: false,
        children: [{
            id: "Low-tech",
            title: "Low-tech",
            hasCheckbox: true,
        }, {
            id: "High-tech",
            title: "High-tech",
            hasCheckbox: true,
        }
        ]
    }
];

// FacilityFilterControl is a React functional component that renders the
// facility filter form and handles user interactions for selecting facility
// parameters and searching for baseline metrics.
function FacilityFilterControl(props) {
    // Clone props to avoid direct mutation and maintain immutability.
    const sitingMode = structuredClone(props.sitingMode)
    const allowNewSite = structuredClone(props.allowNewSite)
    const searchRad = structuredClone(props.searchRad)

    // Handler for changes to any facility filter checkbox. Updates the
    // selected facility parameters and hides results panels until a new
    // search is performed.
    const handleFacilityFormCheckChange = (evt) => {
        props.baselineResultsVisibleSetter(false)
        props.pathwayResultsVisibleSetter(false)
        var facilityParams = structuredClone(props.facilityParams)
        const groupId = evt.target.parentElement.parentElement.parentElement.id;
        const itemId = evt.target.id
        // Implementation for allowing single selection of each facility param
        props.facilityParamsSetter(facilityParams)
        switch (groupId) {
            case 'crop':
                facilityParams.crop === itemId ?
                    facilityParams.crop = '' :
                    facilityParams.crop = itemId
                break
            case 'facilityType':
                facilityParams.type === itemId ?
                    facilityParams.type = '' :
                    facilityParams.type = itemId
                break
            case 'facilitySize':
                facilityParams.size === itemId ?
                    facilityParams.size = '' :
                    facilityParams.size = itemId
                break
            case 'facilityTech':
                facilityParams.tech === itemId ?
                    facilityParams.tech = '' :
                    facilityParams.tech = itemId
                break
        }
        // Implementation for allowing multiple selections of facility params
        // ... (commented out for future use) ...
        props.facilityParamsSetter(facilityParams)
    }

    // SubLevelComp is a recursive component for rendering nested facility
    // filter options, supporting collapsible groups and checkboxes.
    const SubLevelComp = ({item, renderNestedLevels}) => {
        var activeCollapses = structuredClone(props.activeCollapses)
        const selected = activeCollapses.includes(item.id);

        // Toggle the expanded/collapsed state of a group.
        const toggle = () => {
            console.log(activeCollapses)
            console.log(item.id)
            if (activeCollapses.includes(item.id)) {
                activeCollapses = activeCollapses.filter(x => x != item.id)
            } else {
                activeCollapses.push(item.id)
            }
            props.activeCollapsesSetter(activeCollapses)
            console.log(activeCollapses)
        };

        // Check if the item has child options.
        const hasChildren = (item) => {
            return Array.isArray(item.children) && item.children.length > 0;
        };

        return (
            <div>
                <p
                    style={{
                        lineHeight: '20px',
                        cursor: hasChildren(item) ? "pointer" : "",
                        fontWeight: hasChildren(item) ? 500 : 300
                    }}
                    className={'d-flex align-items-center m-0'}
                    id={item.id}
                >
                    {/* Render a checkbox if the item supports selection. */}
                    {item.hasCheckbox &&
                        <input className={'me-2 ms-2'}
                               type="checkbox"
                               id={item.id}
                               onChange={
                                   (evt) => handleFacilityFormCheckChange(evt)
                               }
                            checked={
                                props.facilityParams.crop === item.id
                                || props.facilityParams.type === item.id
                                || props.facilityParams.size === item.id
                                || props.facilityParams.tech === item.id
                            }
                        />
                    }
                    {/* Render a collapse/expand arrow if the item has children. */}
                    {hasChildren(item) &&
                        <span
                            className={'me-2'}
                            onClick={() => toggle()}
                        >{selected ? "▼" : "▶"}
              </span>
                    }
                    {item.title}
                </p>
                {/* Recursively render child options if expanded. */}
                {selected && (
                    <div style={{marginLeft: "10px"}}
                         id={item.id}
                    >
                        {hasChildren(item) && renderNestedLevels(item.children)}
                    </div>
                )}
            </div>
        );
    };

    // NestedCollapse is a helper component for rendering the full nested
    // facility filter tree.
    const NestedCollapse = ({dataTree}) => {
        const renderNestedLevels = (data) => {
            return data.map((item, itemIndex) => (
                <SubLevelComp item={item}
                              renderNestedLevels={renderNestedLevels}
                              key={itemIndex}/>
            ));
        };
        return renderNestedLevels(dataTree);
    }

    // Handler for changes to the siting search radius input.
    const handleSitingRadiusChange = (evt) => {
        let {value, min, max} = evt.target;
        const newVal = Math.max(Number(min), Math.min(Number(max), Number(value)));
        props.searchRadSetter(newVal)
    }

    // Handler for the "Get Baseline Operational Metrics" button. Finds the
    // nearest facility matching the selected parameters and updates the
    // baseline results.
    const handleSearchBtnClick = (evt) => {
        const facilityParams = structuredClone(props.facilityParams)
        const baselineResults = findNearestFacility(
            props.sitingMarkerLngLat[1],
            props.sitingMarkerLngLat[0],
            facilityParams.type,
            facilityParams.size,
            facilityParams.tech,
            facilityParams.crop,
            baselineFacilitiesMetrics
        )
        console.log('baseline')
        console.log(baselineResults)
        props.baselineResultsSetter(baselineResults)
        props.baselineResultsVisibleSetter(true)
    }

    // KEEP: Implementation for async requests to backend.
    // ... (commented out for future use) ...

    // Render the accordion UI for facility filter controls, including the
    // nested filter form and search button.
    return (
        <Accordion
            defaultActiveKey="0"
            className="control-panel-accord facility-control-accord"
            style={props.style}
        >
            <Accordion.Item eventKey="0">
                <Accordion.Header>
                    {/* The header for the accordion section. */}
                    <span className={'accord-title'}>
                            CEA Facility Specifications
                        </span>
                </Accordion.Header>
                <Accordion.Body className={'mt-0 pt-2'}>
                    <NestedCollapse dataTree={facilityFormData}/>
                    <hr className={'mt-2 mb-2'}/>
                    {/* Button to trigger the facility search and display results. */}
                    <Button variant="outline-secondary"
                            className={'mt-0'}
                            style={{fontSize: '14px', width: '100%'}}
                            onClick={(evt) => handleSearchBtnClick(evt)}
                            disabled={props.allowNewSite}
                    >
                        Get Baseline Operational Metrics
                    </Button>
                </Accordion.Body>
            </Accordion.Item>
        </Accordion>
    );
}

export default React.memo(FacilityFilterControl);
